import { Box } from "@mui/material";
import { forwardRef } from "react";
import { formatTermsDate } from "./termsFormat";
import "./termsNoticeBanner.css";

interface ITermsNoticeBannerProps {
	/** The clamped effective date from the API. */
	effectiveDate: string;
	/** From `canAccept` — a team member cannot accept, so they get the nudge instead. */
	canAccept: boolean;
	/** Derived from the API's `cooldownExpiresAt`, which is shared across the vendor's team. */
	isReminderOnCooldown: boolean;
	isReminding: boolean;
	reminderRecipientName: string | null;
	onReviewTerms: () => void;
	onRemindSuperAdmin: () => void;
}

/**
 * Shown for `status === 'notice'` on every page, in the same neutral treatment
 * for both roles — a team member is not blocked during the notice window, so
 * the red variant would alarm them for a month about nothing.
 *
 * A team member does get **Remind Super Admin** here. NAZ-644 §7 originally
 * scoped the reminder to `blocked`; it was extended to the notice period so the
 * team can prompt the one person who can accept before the deadline rather than
 * only after it has passed.
 *
 * There is deliberately no dismiss control and nothing is written to storage:
 * the banner is a standing notice, not a per-session one.
 *
 * Forwards a ref so the layout can measure it: pages that size themselves
 * against the viewport need the banner's real height to subtract.
 */
const TermsNoticeBanner = forwardRef<HTMLDivElement, ITermsNoticeBannerProps>(
	(
		{
			effectiveDate,
			canAccept,
			isReminderOnCooldown,
			isReminding,
			reminderRecipientName,
			onReviewTerms,
			onRemindSuperAdmin,
		},
		ref,
	) => (
		<Box className="termsNoticeBanner" role="status" ref={ref}>
			<p className="termsNoticeText">
				Our Supplier Terms &amp; Conditions change on {formatTermsDate(effectiveDate)}.
			</p>

			<Box className="termsNoticeActions">
				<button type="button" className="termsNoticeLink" onClick={onReviewTerms}>
					Review new terms
				</button>

				{canAccept && (
					<button type="button" className="termsNoticeLink" onClick={onReviewTerms}>
						Accept now
					</button>
				)}

				{/* Confirms in place — the banner is persistent, so there is nowhere to close to. */}
				{!canAccept && reminderRecipientName && (
					<span className="termsNoticeSent">✓ Reminder sent to {reminderRecipientName}</span>
				)}

				{!canAccept && !reminderRecipientName && (
					<button
						type="button"
						className="termsNoticeLink"
						disabled={isReminderOnCooldown || isReminding}
						onClick={onRemindSuperAdmin}>
						Remind Super Admin
					</button>
				)}
			</Box>
		</Box>
	),
);

TermsNoticeBanner.displayName = "TermsNoticeBanner";

export default TermsNoticeBanner;
